/**
 * Novedades de Velora (86c52b7mu) — constantes y utilidades compartidas por el listado, el
 * formulario, la ficha y la vista previa. Contrato: docs/features/novedades-86c52b7mu/spec.md,
 * sección «Admin — /api/admin/news-posts».
 */
import { format } from 'date-fns';

export const NEWS_POSTS_RESOURCE = 'news-posts';

// Mismo orden y mismos códigos que `SupportedLocales::ALL` del back. `es` es el original.
export const NEWS_LOCALES = ['es', 'en', 'ca', 'fr', 'pt', 'it'];
export const SOURCE_LOCALE = 'es';

export const LOCALE_NAMES = {
    es: 'Español',
    en: 'Inglés',
    ca: 'Catalán',
    fr: 'Francés',
    pt: 'Portugués',
    it: 'Italiano',
};

export const TITLE_MAX_LENGTH = 80;
export const BODY_MAX_VISIBLE_LENGTH = 600;
/** Caracteres del texto que enseña el pop-up; el resto, con «…», se lee completo en Novedades. */
export const POPUP_BODY_MAX_LENGTH = 200;
export const BODY_MAX_HTML_LENGTH = 4000;
/** Lo que cabe en `news_post_user.url` del back, donde se guarda el enlace que pulsa cada usuario. */
export const LINK_MAX_LENGTH = 700;
export const IMAGE_MAX_SIZE = 2 * 1024 * 1024;
export const IMAGE_ACCEPT = 'image/png,image/jpeg,image/webp';
export const IMAGE_REQUIRED = 'Sube una imagen para la novedad.';

// Colores de las etiquetas de estado, los del prototipo validado.
export const STATUS = {
    scheduled: { label: 'Programada', color: 'primary', bg: '#E6F3FC', fg: '#005283' },
    active: { label: 'Activa', color: 'success', bg: '#E1FAEB', fg: '#025832' },
    finished: { label: 'Finalizada', color: 'grey', bg: '#F5F5F5', fg: '#374151' },
};

export const STATUS_CHOICES = Object.entries(STATUS).map(([id, { label }]) => ({ id, name: label }));

export const TRANSLATION_STATUS = {
    auto: { label: 'Automática', color: 'primary' },
    manual: { label: 'Corregida a mano', color: 'success' },
    missing: { label: 'Sin traducir', color: 'warning' },
};

/**
 * Texto visible de un fragmento HTML: sin etiquetas y con las entidades resueltas. Es lo que
 * cuenta para el límite de caracteres del cuerpo (`BODY_MAX_VISIBLE_LENGTH`).
 */
export const htmlToText = (html) => {
    if (!html) return '';
    const container = document.createElement('div');
    container.innerHTML = html;
    return container.textContent || '';
};

export const visibleLength = (html) => htmlToText(html).length;

/** Los `href` de los enlaces de un fragmento HTML, como los lee el navegador (`&amp;` es `&`). */
export const linkUrls = (html) => {
    if (!html) return [];
    const container = document.createElement('div');
    container.innerHTML = html;
    return Array.from(container.querySelectorAll('a[href]'), (link) => link.getAttribute('href').trim()).filter(Boolean);
};

/**
 * El HTML recortado a `max` caracteres visibles como mucho, con «…» al final si sobra texto. Corta
 * por texto y no por etiquetas, sin partir palabras, así que la negrita o el enlace que queda a
 * medias sigue cerrado. Es la misma regla que el pop-up de freezer (`truncateHtml` de `features/news`).
 */
export const truncateHtml = (html, max) => {
    if (!html || visibleLength(html) <= max) return html || '';
    const container = document.createElement('div');
    container.innerHTML = html;
    let left = max;
    const texts = [];
    const collect = (node) => {
        node.childNodes.forEach((child) => {
            if (child.nodeType === Node.TEXT_NODE) texts.push(child);
            else collect(child);
        });
    };
    collect(container);
    texts.forEach((node) => {
        if (left <= 0) {
            node.textContent = '';
        } else if (node.textContent.length > left) {
            // Sin partir palabras: se vuelve al último espacio del trozo, si lo hay.
            const slice = node.textContent.slice(0, left);
            const space = slice.lastIndexOf(' ');
            const cut = space > 0 && node.textContent[left] !== ' ' ? slice.slice(0, space) : slice;
            node.textContent = `${cut.trimEnd()}…`;
            left = 0;
        } else {
            left -= node.textContent.length;
        }
    });
    // Fuera los bloques que se han quedado vacíos tras el corte (párrafos, puntos de lista).
    container.querySelectorAll('p, li, ul, ol').forEach((el) => {
        if (el.textContent.trim() === '') el.remove();
    });
    return container.innerHTML;
};

export const isBlankHtml = (html) => htmlToText(html).trim() === '';

/**
 * Valor de un campo traducible en el idioma pedido; si ese idioma está vacío, el español.
 * Es la misma regla con la que el back sirve la novedad en el ATS.
 */
export const pickLocalized = (values, locale) => {
    const value = values?.[locale];
    if (typeof value === 'string' && !isBlankHtml(value)) {
        return { value, locale, fallback: false };
    }
    return { value: values?.[SOURCE_LOCALE] || '', locale: SOURCE_LOCALE, fallback: locale !== SOURCE_LOCALE };
};

/**
 * `notify` con un texto ya redactado y no una clave de traducción: el `_` es el valor por defecto
 * de polyglot, que así no avisa de una clave inexistente.
 */
export const textNotification = (text, options = {}) => [text, { ...options, messageArgs: { _: text } }];

export const todayISO = () => format(new Date(), 'yyyy-MM-dd');

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** «1 oct 2026», el formato de fecha del prototipo validado. Sin fecha, «—». */
export const formatDate = (iso) => {
    if (!iso) return '—';
    const [year, month, day] = iso.slice(0, 10).split('-');
    return `${Number(day)} ${MONTHS[Number(month) - 1]} ${year}`;
};

/** «1 oct 2026 – 31 oct 2026». */
export const formatPeriod = (startsAt, endsAt) =>
    startsAt || endsAt ? `${formatDate(startsAt)} – ${formatDate(endsAt)}` : '';

/**
 * Validación del formulario completo (CA-09). Va a nivel de formulario y no por input porque
 * `TranslatableInputs` aplica los `validate` de cada input a todos los idiomas, y aquí solo el
 * español es obligatorio. `record` es el registro guardado (edición) o `undefined` (alta).
 */
// Forma canónica para comparar con lo guardado, la misma que `NewsPostHtmlSanitizer::comparable()` y
// `comparableTitle()` del back: lo que Tiptap normaliza al cargar (b→strong, i→em, espacios) no cuenta
// como cambio.
const comparableHtml = (html) =>
    (html || '')
        .replace(/<(\/?)b>/gi, '<$1strong>')
        .replace(/<(\/?)i>/gi, '<$1em>')
        .replace(/\u00a0|&nbsp;/g, ' ')
        .replace(/>\s+</g, '><')
        .replace(/\s+/g, ' ')
        .replace(/<(p|li)>\s+/g, '<$1>')
        .replace(/\s+<\/(p|li)>/g, '</$1>')
        .trim();
const comparableTitle = (title) => (title || '').replace(/[\s\u00a0]+/g, ' ').trim();

/**
 * Traducción automática que llega sin tocar: estado `auto` y el mismo título y texto que lo guardado,
 * con la misma comparación que el back (`NewsPostTranslationService::sameContent`). El límite de caracteres
 * visibles es para lo que escribe una persona; esta la escribió Google y puede pasarse sin bloquear el guardado. Si
 * se toca el título o el texto, pasa a `manual` y el back le aplica el tope: aquí también.
 */
export const isUntouchedAutoTranslation = (record, values, locale) =>
    locale !== SOURCE_LOCALE &&
    record?.translation_status?.[locale] === 'auto' &&
    comparableTitle(values?.title?.[locale]) === comparableTitle(record?.title?.[locale]) &&
    comparableHtml(values?.body?.[locale]) === comparableHtml(record?.body?.[locale]);

/** Traducción automática sin tocar que supera el límite de caracteres visibles: aviso, no error. */
export const isLongAutoTranslation = (record, values, locale) =>
    isUntouchedAutoTranslation(record, values, locale) &&
    visibleLength(values?.body?.[locale]) > BODY_MAX_VISIBLE_LENGTH;

export const LONG_AUTO_TRANSLATION_WARNING = `La traducción automática supera los ${BODY_MAX_VISIBLE_LENGTH} caracteres. Revísala y acórtala para que se vea igual que el resto de idiomas.`;

export const validateNewsPost = (record) => (values) => {
    const errors = {};
    const setError = (field, locale, message) => {
        errors[field] = { ...(errors[field] || {}), [locale]: message };
    };

    NEWS_LOCALES.forEach((locale) => {
        const title = values.title?.[locale] || '';
        const body = values.body?.[locale] || '';
        // Fuera del español, un idioma va completo o vacío (vacío = se traduce solo), como en el back.
        const partial = locale !== SOURCE_LOCALE && (title.trim() === '') !== isBlankHtml(body);

        if ((locale === SOURCE_LOCALE || partial) && title.trim() === '') {
            setError('title', locale, locale === SOURCE_LOCALE ? 'Escribe un título.' : 'Escribe también el título, o deja vacíos los dos para traducirlo solo.');
        } else if (title.length > TITLE_MAX_LENGTH) {
            setError('title', locale, `Máximo ${TITLE_MAX_LENGTH} caracteres.`);
        }

        if ((locale === SOURCE_LOCALE || partial) && isBlankHtml(body)) {
            setError('body', locale, locale === SOURCE_LOCALE ? 'Escribe el texto de la novedad.' : 'Escribe también el texto, o deja vacíos los dos para traducirlo solo.');
        } else if (visibleLength(body) > BODY_MAX_VISIBLE_LENGTH && !isUntouchedAutoTranslation(record, values, locale)) {
            setError('body', locale, `Máximo ${BODY_MAX_VISIBLE_LENGTH} caracteres.`);
        } else if (linkUrls(body).some((url) => url.length > LINK_MAX_LENGTH)) {
            // El back lo exige en todos los idiomas: es el largo de la columna donde guarda el enlace pulsado.
            // Antes que el tope de HTML: un enlace muy largo también lo supera, y este aviso dice el porqué.
            setError('body', locale, `Un enlace no puede superar ${LINK_MAX_LENGTH} caracteres.`);
        } else if (body.length > BODY_MAX_HTML_LENGTH) {
            setError('body', locale, 'El texto tiene demasiado formato; simplifícalo.');
        }
    });

    // La imagen es obligatoria (revisión del 26-oct-2026); el back también la exige.
    if (!values.image) {
        errors.image = IMAGE_REQUIRED;
    }

    // Un solo mensaje para las dos fechas, como en el prototipo; se marca cada input que falla.
    if (!values.starts_at || !values.ends_at) {
        const message = 'Indica las dos fechas de vigencia.';
        if (!values.starts_at) errors.starts_at = message;
        if (!values.ends_at) errors.ends_at = message;
    } else if (values.ends_at < values.starts_at) {
        errors.ends_at = 'La fecha de fin no puede ser anterior a la de inicio.';
    } else if ((!record || record.ends_at !== values.ends_at) && values.ends_at < todayISO()) {
        // El back solo exige fin ≥ hoy al crear o si cambia la fecha de fin: una publicación
        // finalizada se puede seguir editando sin moverle la fecha.
        errors.ends_at = 'Con estas fechas la vigencia ya habría terminado.';
    }

    return errors;
};
